import React, { useEffect, useState } from "react";
import { Boxes, Search, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Badge } from "@/components/common";

export default function Inventory() {
  const { branch } = useAuth();
  const [rows, setRows] = useState([]);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!branch) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const [inv, d] = await Promise.all([
          api.get("/inventory", { branch_id: branch.id }),
          api.get("/inventory/dashboard", { branch_id: branch.id }).catch(() => null),
        ]);
        if (!alive) return;
        setRows(Array.isArray(inv) ? inv : inv.rows || []);
        setDash(d);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [branch]);

  if (!branch) return <EmptyState icon={Boxes} title="Pilih cabang" message="Pilih cabang/unit di kanan atas untuk melihat stok." />;
  if (loading) return <Loading />;

  const filtered = rows.filter((r) => !q || `${r.name} ${r.sku}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHeader title="Inventory / Stok" subtitle={branch.name} icon={Boxes} />

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Nilai Stok" value={rupiah(dash.inventory_value)} tone="primary" />
          <StatCard label="Stok Menipis" value={num(dash.low_count)} tone="warning" icon={AlertTriangle} />
          <StatCard label="Habis / Minus" value={num(dash.out_count)} tone="danger" icon={AlertTriangle} />
          <StatCard label="Bunga Kritis (aging)" value={rupiah(dash.aging?.critical?.value || 0)} tone="danger" />
        </div>
      )}

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari item..." className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" data-testid="inv-search" />
        </div>
      </Card>

      {!filtered.length ? (
        <EmptyState icon={Boxes} title="Belum ada stok" message="Tambahkan bahan / barang masuk terlebih dahulu." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium">Item</th>
                  <th className="py-3 px-4 font-medium">Jenis</th>
                  <th className="py-3 px-4 font-medium text-right">Fisik</th>
                  <th className="py-3 px-4 font-medium text-right">Reserved</th>
                  <th className="py-3 px-4 font-medium text-right">Tersedia</th>
                  <th className="py-3 px-4 font-medium text-right">Nilai</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.item_id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`inv-row-${r.item_id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-800">{r.name}</div>
                      {r.oldest_batch_days != null && <div className="text-xs text-slate-400">Batch tertua {r.oldest_batch_days} hari</div>}
                    </td>
                    <td className="py-3 px-4"><Badge className={r.item_type === "product" ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"}>{r.item_type === "product" ? "Produk" : r.category}</Badge></td>
                    <td className="py-3 px-4 text-right">{num(r.qty_physical)} <span className="text-xs text-slate-400">{r.unit}</span></td>
                    <td className="py-3 px-4 text-right text-slate-400">{num(r.qty_reserved)}</td>
                    <td className={`py-3 px-4 text-right font-semibold ${r.qty_available <= (r.min_stock || 0) ? "text-rose-600" : "text-slate-800"}`}>{num(r.qty_available)}</td>
                    <td className="py-3 px-4 text-right text-slate-500">{r.stock_value != null ? rupiah(r.stock_value) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}
